import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  TextField, 
  LinearProgress, 
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Modal
} from '@mui/material';
import { 
  Upload as UploadIcon, 
  Link as LinkIcon, 
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import axios from 'axios';
import http from '../../../utils/http';
import { useTranscriptionStripeCardDetails } from '../../../hooks/useTranscriptionStripeCardDetails';
import {
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import TranscriptionPaymentForm from '../../../components/transcription/TranscriptionPaymentForm';
import { TranscriptionCostModal } from '../../../components/transcription/TranscriptionCostModal';
import { useTranscriptionSocket } from '../../../hooks/useTranscriptionSocket';
import { toast } from 'react-toastify';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface DropzoneProps {
  isDragActive: boolean;
  theme: any;
}

const Dropzone = styled('div')<DropzoneProps>(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.grey[400]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: 'rgba(25, 118, 210, 0.04)'
  }
}));

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

const UploadAudio = () => {
  const [file, setFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [message, setMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [transcriptionId, setTranscriptionId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [hasAddedCard, setHasAddedCard] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize Stripe
  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string
  );

  // Check for saved cards using the transcription hook
  const { isChecking, hasSavedCard, customerId } = useTranscriptionStripeCardDetails({
    setHasAddedCard,
  });

  // WebSocket hook for transcription completion
  const { transcriptionCompleted, clearTranscriptionCompleted } = useTranscriptionSocket();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const audioFile = acceptedFiles[0];
    if (audioFile) {
      setFile(audioFile);
      setAudioUrl('');
      setPreviewUrl(URL.createObjectURL(audioFile));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.webm', '.mp4']
    },
    maxFiles: 1
  });

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setAudioUrl('');
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!audioUrl) return;

    // Check if user has saved card
    if (!hasSavedCard) {
      // Show payment modal first
      setIsPaymentModalOpen(true);
      return;
    }

    proceedWithUrlSubmit();
  };

  const proceedWithUrlSubmit = async () => {
    if (!audioUrl) return;

    try {
      setStatus('uploading');
      setMessage('Processing audio URL...');
      
      const response = await axios.post('/api/transcription/url', { audioUrl });
      
      if (response.data.jobId) {
        setTranscriptionId(response.data.jobId);
        setStatus('success');
        setMessage('Transcription started! You will be redirected to the results page shortly.');
        
        // Poll for transcription status
        pollForTranscriptionStatus(response.data.jobId);
      }
    } catch (error) {
      console.error('Error processing URL:', error);
      setStatus('error');
      setMessage('Failed to process the audio URL. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleFileUpload = async () => {
    if (!file) return;

    // Check if user has saved card
    if (!hasSavedCard) {
      // Show payment modal first
      setIsPaymentModalOpen(true);
      return;
    }

    proceedWithUpload();
  };

  const proceedWithUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('audio', file, file.name);

    try {
      setStatus('uploading');
      setMessage('Uploading your audio file...');
      
      const response = await http.post('/transcription/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent:any) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress(progress);
        },
      });

      if (response.data.jobId) {
        setTranscriptionId(response.data.jobId);
        setStatus('success');
        setMessage('Upload successful! Processing your audio...');
        
        // Poll for transcription status
        pollForTranscriptionStatus(response.data.jobId);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setStatus('error');
      setMessage('Upload failed. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const pollForTranscriptionStatus = async (jobId: string) => {
    try {
      const checkStatus = async () => {
        const response = await axios.get(`/api/transcription/status/${jobId}`);
        const { status, id } = response.data;
        
        if (status === 'COMPLETED' && id) {
          // Redirect to transcription result page
          navigate(`/transcription/${id}`);
          return true;
        } else if (status === 'FAILED') {
          setStatus('error');
          setMessage('Transcription failed. Please try again.');
          setSnackbarOpen(true);
          return true;
        }
        return false;
      };

      // Initial check
      const isComplete = await checkStatus();
      
      // If not complete, poll every 5 seconds
      if (!isComplete) {
        const interval = setInterval(async () => {
          const isComplete = await checkStatus();
          if (isComplete) {
            clearInterval(interval);
          }
        }, 5000);
      }
    } catch (error) {
      console.error('Error checking transcription status:', error);
      setStatus('error');
      setMessage('Error checking transcription status.');
      setSnackbarOpen(true);
    }
  };

  const resetForm = () => {
    setFile(null);
    setAudioUrl('');
    setUploadProgress(0);
    setStatus('idle');
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const fetchSetupIntent = async () => {
    try {
      const response = await http.post("/transcription/create-setup-intent");
      
      if (response.status === 200 && response?.data?.clientSecret) {
        setClientSecret(response?.data?.clientSecret);
      } else {
        throw new Error("Failed to initialize payment setup");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || "Unable to initialize payment setup."
      );
      setIsPaymentModalOpen(false);
    }
  };

  useEffect(() => {
    if (isPaymentModalOpen) {
      fetchSetupIntent();
    }
  }, [isPaymentModalOpen]);

  const handlePaymentSuccess = () => {
    setHasAddedCard(true);
    // Proceed with the upload after successful card addition
    if (file) {
      proceedWithUpload();
    } else if (audioUrl) {
      proceedWithUrlSubmit();
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Audio Transcription
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        Upload an audio file or provide a URL to transcribe it to text. We support MP3, WAV, OGG, M4A, WebM, and MP4 formats.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* File Upload Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Upload Audio File
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Drag and drop your audio file here, or click to browse
              </Typography>
              
              <Dropzone {...getRootProps()} isDragActive={isDragActive}>
                <input {...getInputProps()} />
                {isDragActive ? (
                  <Typography>Drop the audio file here...</Typography>
                ) : (
                  <Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{ mb: 1 }}
                    >
                      Select File
                    </Button>
                    <Typography variant="caption" display="block" color="textSecondary">
                      Max file size: 50MB
                    </Typography>
                  </Box>
                )}
              </Dropzone>
              
              {file && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <Box mr={1}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </Box>
                      <Typography variant="body2">{file.name}</Typography>
                    </Box>
                    <IconButton size="small" onClick={resetForm}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box mt={1}>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress} 
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {Math.round(file.size / 1024)} KB
                    </Typography>
                  </Box>
                </Box>
              )}
              
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={!file || status === 'uploading' || isChecking}
                onClick={handleFileUpload}
                sx={{ mt: 2 }}
                startIcon={status === 'uploading' ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
              >
                {isChecking ? 'Checking...' : status === 'uploading' ? 'Uploading...' : 'Transcribe Audio'}
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* OR Divider */}
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="textSecondary" sx={{ mx: 2 }}>OR</Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Grid>
        
        {/* URL Input Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ textAlign: 'center' }}>
              <LinkIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Transcribe from URL
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Enter a direct URL to an audio file (MP3, WAV, OGG, M4A, WebM, MP4)
              </Typography>
              
              <Box component="form" onSubmit={handleUrlSubmit} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="https://example.com/audio.mp3"
                  value={audioUrl}
                  onChange={(e) => setAudioUrl(e.target.value)}
                  disabled={status === 'uploading' || isChecking}
                  InputProps={{
                    startAdornment: <LinkIcon color="action" sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!audioUrl || status === 'uploading' || isChecking}
                  sx={{ mt: 2 }}
                  startIcon={status === 'uploading' ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isChecking ? 'Checking...' : status === 'uploading' ? 'Processing...' : 'Transcribe from URL'}
                </Button>
              </Box>
              
              {previewUrl && (
                <Box sx={{ mt: 3 }}>
                  <audio controls style={{ width: '100%' }}>
                    <source src={previewUrl} type={`audio/${file?.name.split('.').pop()}`} />
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Status Message */}
      {status === 'uploading' && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.contrastText">
            {message}
          </Typography>
          <LinearProgress color="info" sx={{ mt: 1 }} />
        </Box>
      )}
      
      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
      
      {/* Payment Modal */}
      <Modal
        open={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        aria-labelledby="payment-modal-title"
        aria-describedby="payment-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '90%' : 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <Typography id="payment-modal-title" variant="h6" component="h2" gutterBottom>
            Add Payment Method
          </Typography>
          <Typography id="payment-modal-description" variant="body2" color="textSecondary" paragraph>
            We need a payment method on file to process your transcription. You will be charged per transcription completed.
          </Typography>
          
          {clientSecret ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: "stripe",
                  variables: {
                    colorPrimary: "#3d874e",
                    colorBackground: "#ffffff",
                    colorText: "#2e3011",
                    fontFamily: "Inter, system-ui, sans-serif",
                  },
                },
              }}
            >
              <TranscriptionPaymentForm
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
                onClose={() => setIsPaymentModalOpen(false)}
              />
            </Elements>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Modal>
      
      {/* Transcription Cost Modal */}
      <TranscriptionCostModal
        isVisible={!!transcriptionCompleted}
        onClose={clearTranscriptionCompleted}
        transcriptionData={transcriptionCompleted}
      />
      
      {/* Hidden file input for the button */}
      <VisuallyHiddenInput 
        type="file" 
        accept="audio/*" 
        ref={fileInputRef}
        onChange={handleFileInputChange}
      />
    </Box>
  );
};

export default UploadAudio;